// W07 Select (q-select outlined via UiSelect), the multi-select with chips (discount rules), W11 Toggle (ToggleInput
// 40×20, knob 18, on q-primary), W12 SegmentedToggle (TypeToggleInput, the % / $ pair), W13 Checkbox and RadioGroup.
import { useId, useRef, useState, type ReactNode } from 'react';
import { Checkbox as RadixCheckbox, Popover, RadioGroup as RadixRadio, Select as RadixSelect, Switch } from 'radix-ui';
import { CircleX } from 'lucide-react';
import { backofficeColor } from '@radish/ui-tokens';
import { ClearButton, FIELD_APPEND_AFTER_ERROR, FIELD_TEXT_INK, FIELD_DISABLED_BOX, FIELD_MARGINAL, FieldErrorIcon, FieldFrame, FieldMessage } from './field.tsx';

// ClearButton lives in field.tsx so SearchField can draw it (B2037); choice.tsx keeps exporting it.
export { ClearButton };

export interface Option<V extends string = string> {
  readonly value: V;
  readonly label: string;
  /** A colour dot before the label in a multi-select's menu (legacy SelectCategory's BaseColorBadge avatar). */
  readonly color?: string;
}

interface SelectFieldProps<V extends string> {
  readonly label: string;
  /** Legacy's plain label over the box, nothing floating inside (field.tsx `labelAbove`, B533). */
  readonly labelAbove?: boolean | 'loose' | 'plain' | undefined;
  readonly value: V | null;
  readonly options: readonly Option<V>[];
  readonly onValueChange: (value: V) => void;
  readonly dense?: boolean;
  readonly disabled?: boolean;
  readonly icon?: ReactNode;
  /** Drawn before the chosen option's text, inside the value row under the label (legacy SelectCategory's colour dot). */
  readonly valueIcon?: ReactNode;
  /** Shows the label inside the box instead of floating it (the report header's "Last 7 Days" buttons). */
  readonly inlineLabel?: boolean;
  readonly className?: string;
  /** Legacy q-select `rules`' message: under the box in negative, in place of the hint, with or without `inlineLabel`. */
  readonly error?: string | undefined;
  /** Legacy q-select `hint`: the line under the box (FieldFrame's, as a text field's; B1328), with or without `inlineLabel`. */
  readonly hint?: string | undefined;
  readonly testId?: string;
  /** Legacy q-select `clearable`: a clear button while a value is chosen. */
  readonly onClear?: () => void;
  /** Legacy SelectCategory / SelectDepartment `enable-create-*`: a "+ Create …" option before the others that opens a create dialog instead of choosing. */
  readonly create?: { readonly label: string; readonly onCreate: () => void };
  /**
   * The menu's item ink. 'grey' (the default): a plain q-select's items. 'body': legacy's `rad-select-dropdown` popup class,
   * --q-text-primary, which legacy SelectCategory, SelectCourse, SelectDepartment, SelectDevices, SelectEmployees and
   * SelectIdVerification give their menus (B983, B1001). The chosen item is primary either way.
   */
  readonly menuInk?: 'grey' | 'body';
  /**
   * Legacy SelectCategory, SelectCourse and SelectIdVerification draw a BaseColorBadge before every option in the menu (its
   * avatar section) and before the chosen value: an 18 px dot with a 1 px border, the option's colour or, with none (courses
   * and ID verification lists have no colour), q-badge's primary (B1013, measured on GG LA's Register Items and Assign Items).
   * A `valueIcon` still replaces the value's dot.
   */
  readonly dots?: boolean;
  /**
   * Legacy BaseTable's Records per page picker (`pp-rows-per-page-dropdown`, a dense outlined q-select with `color="grey"`;
   * needs `inlineLabel`): a 30 px tall box that never shrinks, 60 wide under BaseTableServerSide (`'server'`) and 50 under
   * BaseTable (`'client'`, a list paged in the browser), as each styles it (#2312's review, measured on GG LA's Items and
   * Customers). Legacy's 50 px box clips "30" to "3…" (B1366); Next keeps the arrow where legacy's is (26 px in) and takes
   * the missing pixel from the arrow's 6 px lead, so the value shows whole. Its value 5 px in and the arrow flush right, both $gray in light and dark; the
   * border stays --q-border on hover and turns 2 px $gray while open or focused, and the chosen item is $gray (B1355,
   * measured beside GG LA's Menu > Items).
   */
  readonly pager?: false | 'client' | 'server';
}

/** Legacy BaseColorBadge in a select (q-badge `ui-table-badge`): an 18 px round dot with legacy's `1.5px` border (B1016; Chrome 155 measured it 1 px wide at both 1x and 2x). */
function OptionDot({ color }: { readonly color: string | undefined }) {
  return <span aria-hidden data-testid="option-dot" className={`size-[18px] shrink-0 rounded-full border-[1.5px] border-line ${color === undefined ? 'bg-primary' : ''}`} style={color === undefined ? undefined : { background: backofficeColor(color) }} />;
}


/**
 * Quasar q-select's dropdown icon (`arrow_drop_down`, Material's filled triangle, 24 px), turned over while the menu is
 * open, as every legacy select draws it (lane V round 8 saw our chevron), in the field's marginal colour.
 */
export function DropArrow({ open = false, testId = 'select-arrow', pager = false }: { readonly open?: boolean; readonly testId?: string; readonly pager?: boolean }) {
  return (
    <svg aria-hidden width={24} height={24} viewBox="0 0 24 24" data-testid={testId} className={`shrink-0 transition-transform duration-300 ${pager ? 'text-grey' : FIELD_MARGINAL} ${open ? `rotate-180 ${pager ? '' : 'text-primary!'}` : ''}`}>
      {/* The pager's picker sets `dropdown-icon="expand_more"` (Material's chevron) in $gray, open or not (B1355). */}
      <path d={pager ? 'M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z' : 'M7 10l5 5 5-5z'} fill="currentColor" />
    </svg>
  );
}

/**
 * A q-select's appended controls (clear, then the drop arrow): Quasar's `.q-field__append` padding, 12 px before the
 * first (6 px dense) and 2 px between them, in place of the frame's 8 px gap (lane D5 r34: the Drawers status select
 * read 74.7 px wide against legacy's 70.3).
 */
function Append({ dense, flush = false, lead, children }: { readonly dense: boolean | undefined; readonly flush?: boolean; readonly lead?: string; readonly children: ReactNode }) {
  return <span className={`-ml-2 flex shrink-0 items-center gap-[2px] ${lead ?? (dense === true ? 'pl-[6px]' : 'pl-3')} ${FIELD_APPEND_AFTER_ERROR} ${flush ? '-mr-px' : ''}`}>{children}</span>;
}

/**
 * Quasar's q-menu under a q-select (walked on legacy Drawers' status and lane V r24's staff Accounts filters): no border,
 * 4 px corners, Quasar's menu shadow, no padding, flush with the field's box at its left edge and at least its width;
 * 48 px items 16 px in, grey text with the chosen one in primary, and no item highlighted until hovered or reached by key.
 * In dark (B1287): legacy's 06dp fill with no shadow (_dark.scss), and Quasar's q-item--dark ink, #fff.
 */
const Q_MENU = 'z-[3000] overflow-hidden rounded-[4px] bg-menu shadow-[0_1px_5px_rgba(0,0,0,0.2),0_2px_2px_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)] dark:shadow-none';
const Q_ITEM = 'flex h-12 cursor-pointer items-center px-4 text-[14px] outline-none hover:bg-menu-item-hover';
/** The item a key has moved to: Radix focuses the chosen item on open, which legacy never marks. */
const Q_ITEM_KEYED = 'data-[highlighted]:bg-menu-item-hover';

/** Where a Radix Select menu must sit to hang from the field's box rather than from its trigger inside the box. */
interface BoxPlacement { readonly alignOffset: number; readonly sideOffset: number; readonly width: number }

function placeUnder(box: HTMLElement | null, trigger: HTMLElement | null): BoxPlacement | null {
  if (box === null || trigger === null) return null;
  const b = box.getBoundingClientRect();
  const t = trigger.getBoundingClientRect();
  return { alignOffset: b.left - t.left, sideOffset: b.bottom - t.bottom, width: b.width };
}

/** The value the create option carries; never a real option's value. */
const CREATE = '__create__';

export function SelectField<V extends string>({ label, value, options, onValueChange, dense, disabled, icon, valueIcon, inlineLabel, className, error, hint, testId, onClear, create, labelAbove, menuInk = 'grey', dots = false, pager: pagerWidth = false }: SelectFieldProps<V>) {
  const pager = pagerWidth !== false;
  const above = labelAbove !== undefined && labelAbove !== false;
  const id = useId();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [placement, setPlacement] = useState<BoxPlacement | null>(null);
  const [keyed, setKeyed] = useState(false);
  const openChange = (next: boolean) => {
    if (next) {
      setPlacement(placeUnder(boxRef.current, triggerRef.current));
      setKeyed(false);
    }
    setOpen(next);
  };
  const selected = options.find((o) => o.value === value);
  const invalid = error !== undefined && error !== '';
  const clear = onClear === undefined || selected === undefined || disabled === true ? null : <ClearButton label={label} onClear={onClear} refocus={triggerRef} />;
  const chevron = <DropArrow open={open} />;
  // An inline-label box draws the field's bottom row too when it has an error or hint (legacy ModifierBase's Minimum
  // selections q-select shows its rule's message under it); with neither it stays the bare box.
  const inlineMessage = (box: ReactNode) => (invalid || hint !== undefined ? <div>{box}<FieldMessage id={id} error={error} hint={hint} disabled={disabled} /></div> : box);
  const trigger = (
    <RadixSelect.Trigger
      ref={triggerRef}
      id={id}
      data-testid={testId}
      aria-label={inlineLabel ? `${label}: ${selected?.label ?? ''}` : undefined}
      aria-describedby={invalid || hint !== undefined ? `${id}-message` : undefined}
      aria-invalid={invalid || undefined}
      className={`flex h-full w-full min-w-0 items-center bg-transparent text-left text-[14px] tracking-[0.00937em] ${pager ? 'text-grey' : FIELD_TEXT_INK} outline-none [&>span]:min-w-0 [&>span]:truncate ${inlineLabel || above ? '' : dense ? 'pt-3' : 'pt-4'}`}
    >
      {/* One line, cut with an ellipsis, as q-select's native text (lane V r24: a long Reseller wrapped over its label); Radix's
          Value span takes no class, so the trigger styles it. */}
      <RadixSelect.Value placeholder="">{selected === undefined ? undefined : valueIcon !== undefined || dots ? <span className="inline-flex items-center gap-2 align-top">{valueIcon ?? <OptionDot color={selected.color} />}{selected.label}</span> : selected.label}</RadixSelect.Value>
    </RadixSelect.Trigger>
  );
  return (
    // Radix's hidden native select reports '' when the value arrives before its option does (a list still loading), which
    // would wipe the value (Edit Account's Select Reseller, lane V round 22). No option is ever '', so '' is never a pick.
    <RadixSelect.Root value={value ?? ''} onValueChange={(v) => { if (v === '') return; if (v === CREATE) create?.onCreate(); else onValueChange(v as V); }} open={open} onOpenChange={openChange} disabled={disabled ?? false}>
      {inlineLabel ? inlineMessage(
        <div ref={boxRef} data-focused={open ? '' : undefined} className={`group/field relative flex items-center gap-2 rounded-q border bg-field ${pager ? `h-[30px] shrink-0 ${pagerWidth === 'server' ? 'w-[60px]' : 'w-[50px]'} pr-0 pl-1` : `px-[11px] ${dense ? 'h-10' : 'h-14'}`} ${invalid ? 'border-negative ring-1 ring-negative ring-inset' : pager ? (open ? 'border-grey ring-1 ring-grey ring-inset' : 'border-line has-[:focus-visible]:border-grey has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-grey has-[:focus-visible]:ring-inset') : open ? 'border-primary ring-1 ring-primary ring-inset' : disabled === true ? `border-line ${FIELD_DISABLED_BOX}` : 'border-line hover:border-primary has-[:focus]:border-primary has-[:focus]:ring-1 has-[:focus]:ring-primary has-[:focus]:ring-inset'} ${className ?? ''}`}>
          {icon}
          {trigger}
          {/* Quasar's error icon on an invalid inline-label q-select too (ModifierBase's Minimum selections; B1918). */}
          {invalid ? <FieldErrorIcon dense={dense} top={false} /> : null}
          <Append dense={dense} flush={pager} {...(pagerWidth === 'client' ? { lead: 'pl-[5px]' } : {})}><DropArrow open={open} pager={pager} /></Append>
        </div>,
      ) : (
        <FieldFrame
          id={id} label={label} floated={selected !== undefined || open} focused={open} dense={dense} disabled={disabled} error={error} hint={hint}
          start={icon} end={<Append dense={dense}>{clear}{chevron}</Append>} className={className} labelAbove={labelAbove}
          wrapBox={(box) => <div ref={boxRef}>{box}</div>}
        >
          {trigger}
        </FieldFrame>
      )}
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper" align="start" alignOffset={placement?.alignOffset ?? 0} sideOffset={placement?.sideOffset ?? 0}
          style={placement === null ? undefined : { minWidth: placement.width }} className={Q_MENU} onKeyDown={() => setKeyed(true)}
          // Legacy's picker blurs once its menu hides (BaseTable `@popup-hide`), so a click leaves no $gray ring; Next keeps focus
          // on the box for the keyboard but without the ring after a pick by pointer (B1355).
          onCloseAutoFocus={pager && !keyed ? (e) => { e.preventDefault(); triggerRef.current?.focus({ preventScroll: true, focusVisible: false } as FocusOptions); } : undefined}
        >
          <RadixSelect.Viewport>
            {create === undefined ? null : (
              <RadixSelect.Item value={CREATE} className={`${Q_ITEM} ${keyed ? Q_ITEM_KEYED : ''} text-primary`}>
                <RadixSelect.ItemText>{create.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            )}
            {options.map((o) => (
              <RadixSelect.Item
                key={o.value}
                value={o.value}
                className={`${Q_ITEM} ${keyed ? Q_ITEM_KEYED : ''} ${menuInk === 'body' ? 'text-menu-ink-body' : 'text-menu-ink'} ${pager ? 'data-[state=checked]:text-grey' : 'data-[state=checked]:text-primary'}`}
              >
                {/* Legacy's avatar section: 35 px wide, the label 51 px into the item (B1013). */}
                {dots ? <span className="flex w-[35px] shrink-0"><OptionDot color={o.color} /></span> : null}
                <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

interface MultiSelectFieldProps {
  readonly label: string;
  readonly values: readonly string[];
  readonly options: readonly Option[];
  readonly onValuesChange: (values: string[]) => void;
  readonly hint?: string;
  /** A validation message under the field (legacy q-select rules). */
  readonly error?: string;
  /** Legacy q-select `dense`: 40 px tall instead of 56 (the report filters). */
  readonly dense?: boolean;
  /**
   * How the chosen options show. 'chips' (the default): removable chips, legacy `use-chips` (discount rules, the catalog
   * pickers, Payroll Summary's employees). 'commas': the labels joined with ", " on one line, cut with an ellipsis, as a
   * q-select `multiple` without `use-chips` draws them (Item Sales' device, department and employee filters).
   */
  readonly display?: 'chips' | 'commas';
  /** Legacy q-select `disable`: shown at 60 % with its chips, nothing opens and no chip comes off. */
  readonly disabled?: boolean;
  /** 'primary': legacy SelectCategory's own chips (dense q-chip in the primary colour, white text); else Quasar's grey chip. */
  readonly chipTone?: 'neutral' | 'primary';
  /** Legacy q-select `clearable`: a clear button while anything is chosen. */
  readonly onClear?: () => void;
  /**
   * The menu's item ink, as SelectField's: 'grey' (the default) for a plain q-select, whose q-menu takes the body's
   * --q-text-secondary; 'body' for legacy's `rad-select-dropdown` selects (SelectCategory, SelectDepartment, SelectDevices,
   * SelectEmployees), --q-text-primary (B1001). The chosen items are primary either way.
   */
  readonly menuInk?: 'grey' | 'body';
}

/** A multi-select: removable chips (legacy `use-chips`) or the labels joined with commas (a plain `multiple` q-select). */
export function MultiSelectField({ label, values, options, onValuesChange, hint, error, dense, display = 'chips', chipTone = 'neutral', onClear, disabled = false, menuInk = 'grey' }: MultiSelectFieldProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const chosen = options.filter((o) => values.includes(o.value));
  const toggle = (v: string) => onValuesChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  const joined = chosen.map((c) => c.label).join(', ');
  const ariaLabel = chosen.length === 0 ? label : `${label}: ${joined}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const clear = onClear === undefined || chosen.length === 0 || disabled ? null : <ClearButton label={label} onClear={onClear} refocus={triggerRef} />;
  const primary = chipTone === 'primary';
  // Dense chips are Quasar's dense q-chip (21 px, 5.6 px sides, 12 px radius) on rows that wrap, the field growing with them:
  // legacy's dense report filter is 45 px tall with one row of chips under its label (lane D5 r32, Item Sales' Category).
  // q-chip keeps 4 px of margin on every side: the first chip 16 px in, 8 px between chips.
  // Every use-chips q-select draws dense chips, in a standard field too: legacy's outlined field then grows to 61 px, its
  // chips 28 px under the box's top edge (lane BO 2026-10-19, Add Automatic Discount's Include categories on GG LA).
  const chipped = display === 'chips' && chosen.length > 0;
  const denseChips = dense === true && chipped;
  return (
    <Popover.Root open={open && !disabled} onOpenChange={(next) => setOpen(next && !disabled)}>
      {/* The menu hangs from the field's box, as a q-select's does: not from the trigger left of the chips, and not from
          the field with its hint line (lane V r22). */}
      <FieldFrame id={id} label={label} hint={hint} error={error} floated={chosen.length > 0 || open} focused={open} dense={dense} disabled={disabled} autoHeight={chipped} end={<Append dense={dense}>{clear}<DropArrow open={open} /></Append>}
        wrapBox={(box) => <Popover.Anchor asChild>{box}</Popover.Anchor>}>
        {display === 'commas' ? (
          // Legacy's selected text: 14 px, one line, `text-overflow: ellipsis` (lane D5 r32 on Item Sales).
          <Popover.Trigger ref={triggerRef} id={id} aria-label={ariaLabel} disabled={disabled} className={`block h-full w-full min-w-0 truncate text-left text-[14px] tracking-[0.00937em] ${FIELD_TEXT_INK} outline-none ${dense ? 'pt-3' : 'pt-4'}`} data-testid="multi-select-text">
            {joined}
          </Popover.Trigger>
        ) : (
          <div className={denseChips ? 'flex w-full flex-wrap items-center gap-2 pt-[17px] pb-[5px] pl-[3px]' : chipped ? 'flex w-full flex-wrap items-center gap-2 pt-[27px] pb-[11px] pl-[4px]' : `flex h-full w-full items-center gap-1 overflow-hidden ${dense ? 'pt-3' : 'pt-4'}`}>
            {chosen.map((o) => (
              // Quasar's dense q-chip: 21 px, 5.6 px sides, 12 px radius; its grey #e0e0e0 with rgba(0,0,0,.87) text (q-chip--dark:
              // legacy's --q-dark #212224 with #ffffffb3), and the 17.5 px `cancel` remove icon in the text colour (.54 black in
              // light) at 60 % opacity; a primary (SelectCategory) chip's icon is its own text colour at 60 % too, and its text is black in
              // the dark theme.
              <span key={o.value} data-testid="multi-select-chip" className={`inline-flex h-[21px] max-w-full min-w-0 items-center gap-[1.75px] rounded-[12px] px-[5.6px] text-[14px] tracking-[0.00937em] whitespace-nowrap ${primary ? 'bg-primary text-white dark:text-black' : 'bg-[#e0e0e0] text-black/[0.87] dark:bg-[#212224] dark:text-white/70'}`}>
                {/* Legacy's chip label is an `ellipsis` span at max-width 100 %: a long name cuts off with … inside the field. */}
                <span className="min-w-0 truncate" data-testid="multi-select-chip-label">{o.label}</span>
                <button type="button" aria-label={`Remove ${o.label}`} disabled={disabled} onClick={() => toggle(o.value)} className={`shrink-0 rounded-full opacity-60 enabled:hover:opacity-100 ${primary ? 'text-white dark:text-black' : 'text-black/[0.54] dark:text-white/70'}`}>
                  <CircleX size={primary ? 16 : 17.5} aria-hidden fill="currentColor" className={primary ? '[&>path]:stroke-primary [&>path]:stroke-[2.5]' : '[&>circle]:stroke-none [&>path]:stroke-[#e0e0e0] [&>path]:stroke-[2.5] dark:[&>path]:stroke-[#212224]'} />
                </button>
              </span>
            ))}
            <Popover.Trigger ref={triggerRef} id={id} aria-label={ariaLabel} disabled={disabled} className={denseChips ? 'h-[21px] min-w-4 flex-1 outline-none' : 'h-full flex-1 outline-none'} />
          </div>
        )}
      </FieldFrame>
      <Popover.Portal>
        {/* A q-select `multiple` menu, with or without use-chips (lane V r20; legacy Item Sales and Payroll Summary): 48 px
            items with no checkbox, a chosen one in the primary colour (q-item--active), up to Quasar's menu height. The
            menu's top meets the box's bottom edge (lane D5 r34, measured on legacy Payroll and Item Sales). */}
        <Popover.Content
          align="start" sideOffset={0} role="listbox" aria-multiselectable aria-label={label}
          className="z-[3000] max-h-[650px] w-[var(--radix-popover-trigger-width)] min-w-64 overflow-auto rounded-q border border-line bg-menu shadow-lg dark:border-transparent dark:shadow-none"
        >
          {options.length === 0 ? <p className="px-4 py-2 text-text-3">No options</p> : null}
          {options.map((o) => {
            const chosenOption = values.includes(o.value);
            return (
              <button
                key={o.value} type="button" role="option" aria-selected={chosenOption} onClick={() => toggle(o.value)}
                className={`flex h-12 w-full cursor-pointer items-center px-4 text-left text-[14px] hover:bg-menu-item-hover ${chosenOption ? 'text-primary' : menuInk === 'body' ? 'text-menu-ink-body' : 'text-menu-ink'}`}
              >
                {o.color === undefined ? null : (
                  // Legacy SelectCategory's avatar section: an 18 px dot with a 1.5 px border (B1016), the label 35 px after its start.
                  <span className="flex w-[35px] shrink-0" data-testid="multi-select-option-color">
                    <OptionDot color={o.color} />
                  </span>
                )}
                {o.label}
              </button>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

interface CheckboxProps {
  /**
   * 'indeterminate': q-table's header selection while some rows are ticked, the box filled in the table's default colour
   * grey-8 (#616161) with Quasar's bar; a press ticks it (B2000).
   */
  readonly checked: boolean | 'indeterminate';
  readonly onCheckedChange: (checked: boolean) => void;
  readonly label: string;
  /** The label is rendered by the caller (the row's visible text); the box still carries it for assistive tech. */
  readonly hideLabel?: boolean;
  readonly disabled?: boolean;
  readonly description?: string;
  /**
   * A settings page's checkbox, as legacy's UiCustomSetting draws it (measured on Register Settings and Customer Tips,
   * lane D6 2026-10-13): a dense 18 px box 11 px in (`tw-ml-2.5` plus q-checkbox's inset), its label 13 px after it (text at 641 px), the label bold in the secondary colour
   * (`text-weight-bold q-mb-sm` inside `text-secondary`), and the description 8 px under it.
   */
  readonly settingDescription?: boolean;
  /**
   * The label's ink when it is not a setting's title (default `text-text-1`). Legacy wraps some q-checkbox labels in
   * UiDescription, whose title is pure white in dark; such a caller passes `text-text-1 dark:text-white` (B1522).
   */
  readonly labelInk?: string;
  /**
   * Quasar's dense q-checkbox (legacy `BaseCheckbox dense`): the 18 px box centred in a 20 px cell, its words 7 px after
   * the cell (q-checkbox__label's 0.5 em), so they start 26 px after the box's left edge (Employee Breaks' Paid Break,
   * measured beside legacy at 1600 px, lane BL 2026-10-23).
   */
  readonly dense?: boolean;
  readonly id?: string;
  /**
   * 'table-header': q-table's header selection (a Bulk Assign dialog's Select All), which takes the table's `color`,
   * grey-8 (#616161) by default, ticked as well as indeterminate, where the rows' boxes are primary (B2001).
   */
  readonly tone?: 'primary' | 'table-header';
}

/**
 * Legacy's q-checkbox (B1381; Quasar 2.18.6 QCheckbox.sass): a 20 px box (dense 18) with a 2 px radius, unchecked in 54 %
 * black (dark: 70 % white) and with no fill of its own, inside a 40 px target (dense 20) that is all clickable. On hover
 * (of the box or its label) and on focus it shows Quasar's focus helper, a currentColor circle at 12 % (32 % in dark):
 * 40 px, or the dense target's 20 grown 1.4 times to 28. The target and circle are the box's ::after and ::before, so they
 * add no size: callers keep the 40 px cells they draw (StackedCheckbox, CheckCell) and the circle fills them as legacy's
 * does. The circle sits above the box's own fill and below its tick (an isolated stacking context), so a ticked box shows
 * primary at 12 % on primary, as Quasar's does. The house focus-visible ring stays for keyboard users. Measured on GG LA's
 * Report Settings (normal) and Business Hours (dense) at 1600 in both themes.
 */
const HALO = "isolate before:absolute before:-z-10 before:rounded-full before:bg-current before:opacity-12 dark:before:opacity-32 before:scale-0 before:transition-[scale] before:duration-[220ms] before:content-[''] hover:before:scale-100 focus:before:scale-100 group-has-[label:hover]/checkbox:before:scale-100 disabled:before:hidden";
// The circle (::before) and the clickable target (::after, always there) sit on the padding box, inside the 2 px border:
// 16 + 2 × 12 = 40 for both; dense, the target 14 + 2 × 3 = 20 and the circle 14 + 2 × 7 = 28.
const TARGET = "before:-inset-[12px] after:absolute after:-inset-[12px] after:content-['']";
const DENSE_TARGET = "before:-inset-[7px] after:absolute after:-inset-[3px] after:content-['']";

export function Checkbox({ checked, onCheckedChange, label, hideLabel = false, disabled, description, settingDescription = false, dense = false, id: givenId, labelInk = 'text-text-1', tone = 'primary' }: CheckboxProps) {
  const generated = useId();
  const id = givenId ?? generated;
  const box = (
    <RadixCheckbox.Root
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(c) => onCheckedChange(c === true)}
      aria-label={hideLabel ? label : undefined}
      className={`relative inline-flex ${settingDescription || dense ? `size-[18px] ${DENSE_TARGET}` : `size-5 ${TARGET}`} shrink-0 items-center justify-center rounded-[2px] border-2 border-current bg-transparent text-black/54 dark:text-text-2 ${tone === 'table-header' ? 'data-[state=checked]:bg-[#616161] data-[state=checked]:text-[#616161]' : 'data-[state=checked]:bg-primary data-[state=checked]:text-primary'} data-[state=indeterminate]:bg-[#616161] data-[state=indeterminate]:text-[#616161] ${HALO} disabled:cursor-not-allowed disabled:opacity-75`}
    >
      {/* Quasar's tick (B1492): its own path, stroked 3.12 in a 24 grid, filling the box inside its 2 px line (16 px, dense
          14), white, and black in legacy's dark theme (_dark.scss .q-checkbox__svg). Next drew lucide's smaller check. */}
      <RadixCheckbox.Indicator className="absolute inset-0">
        {checked === 'indeterminate'
          ? (
            <svg viewBox="0 0 24 24" className="absolute inset-0 size-full text-white dark:text-black" aria-hidden>
              <path fill="currentColor" d="M4,14H20V10H4" data-testid="checkbox-bar" />
            </svg>
          )
          : (
            <svg viewBox="0 0 24 24" className="absolute inset-0 size-full text-white dark:text-black" aria-hidden data-testid="checkbox-tick">
              <path fill="none" stroke="currentColor" strokeWidth={3.12} d="M1.73,12.91 8.1,19.28 22.79,4.59" />
            </svg>
          )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
  if (hideLabel) return box;
  return (
    <div className={`group/checkbox flex items-start ${settingDescription ? 'ml-[11px] gap-[13px]' : dense ? 'gap-[7px]' : 'gap-3'}`}>
      <div className={dense && !settingDescription ? 'flex w-5 justify-center pt-0.5' : 'pt-0.5'}>{box}</div>
      <div>
        {/* A disabled q-checkbox fades its own label with its box (the label sits inside the q-checkbox); a setting's title
            stands outside it in legacy's UiCustomSetting and keeps its ink (B1390). */}
        <label htmlFor={id} className={`${disabled === true ? `cursor-not-allowed${settingDescription ? '' : ' opacity-75'}` : 'cursor-pointer'} text-[14px] ${settingDescription ? 'font-bold text-text-2' : `font-medium ${labelInk}`}`}>{label}</label>
        {description === undefined ? null : <p className={`${settingDescription ? 'mt-2' : 'mt-1'} text-[14px] leading-[1.5] text-text-2`} data-testid="checkbox-description">{description}</p>}
      </div>
    </div>
  );
}

/**
 * Legacy UiCustomSetting whose action is a DENSE BaseCheckbox (`<BaseCheckbox dense />`: Dining Options' Advanced
 * Settings, ID Verification; measured on the DDEV at 1600 x 996, lane BL 2026-10-22): a 20 px cell holding the 18 px box
 * at the field column's left edge (x 680), 12 px, then the description column (10 px apart): the title bold in the
 * secondary colour, 8 px more under it when legacy gives it `q-mb-sm` (`titleGap`), the description, and anything the
 * setting shows under it.
 * Which to use: read the legacy page's checkbox. A plain `<BaseCheckbox />` (a 40 px q-checkbox, so the box sits 11 px
 * into the column, x 690: Register Settings, Staff Tips) is `Checkbox settingDescription`; a dense one is this.
 */
export function CheckboxSetting({ label, description, checked, onCheckedChange, disabled, titleGap = false, children, testId }: {
  readonly label: string; readonly description?: ReactNode; readonly checked: boolean; readonly onCheckedChange: (checked: boolean) => void;
  readonly disabled?: boolean; readonly titleGap?: boolean; readonly children?: ReactNode; readonly testId?: string;
}) {
  const id = useId();
  return (
    <div className="flex gap-3" data-testid={testId ?? 'checkbox-setting'}>
      <span className="inline-flex size-5 shrink-0 items-center justify-center">
        <Checkbox id={id} label={label} hideLabel dense checked={checked} onCheckedChange={onCheckedChange} {...(disabled === undefined ? {} : { disabled })} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <label htmlFor={id} className={`cursor-pointer text-[14px] leading-[1.5] font-bold text-text-2${titleGap ? ' mb-2' : ''}`}>{label}</label>
        {description === undefined ? null : <div className="text-[14px] leading-[1.5] text-text-2" data-testid="checkbox-setting-description">{description}</div>}
        {children}
      </div>
    </div>
  );
}

interface RadioGroupProps<V extends string> {
  readonly label: string;
  readonly value: V;
  readonly options: readonly (Option<V> & { readonly disabled?: boolean })[];
  readonly onValueChange: (value: V) => void;
  readonly disabled?: boolean;
  /** One row (legacy `q-option-group inline`, e.g. the printer type) instead of a column; with `rows`, legacy's 8 px gutter. */
  readonly inline?: boolean;
  /**
   * `q-option-group`: legacy's non-dense Quasar option group on settings pages (Customer Tips, Overtime's seventh day),
   * a 40 px row per option with the 20 px circle inset 10 px and the label 10 px after it. Unset: the stacked column.
   */
  readonly rows?: 'q-option-group';
  /**
   * A bare legacy q-option-group (Overtime's Seventh-day Rule, measured on the DDEV 2026-10-22): the checked label stays
   * at normal weight (rgb(31, 31, 31) 400). Unset: UiRadioSettingGroup's checked label at 500 (Customer Tips).
   */
  readonly plainLabels?: boolean;
}

/**
 * Legacy UiRadioSettingGroup: a column of radios, the checked label in text-primary 500. A disabled radio fades as a whole
 * row, circle and label, to 0.75 (Quasar 2.18.6 QRadio.sass `.q-radio.disabled { opacity: .75 !important }` on the q-radio,
 * which holds its label; Overtime's Seventh-day Rule and Payment Configuration's Program Type measured so), with the
 * not-allowed cursor (B1390).
 */
export function RadioGroup<V extends string>({ label, value, options, onValueChange, disabled, inline = false, rows, plainLabels = false }: RadioGroupProps<V>) {
  const optionGroup = rows === 'q-option-group';
  return (
    <RadixRadio.Root
      aria-label={label} value={value} onValueChange={(v) => onValueChange(v as V)} disabled={disabled}
      // Inline q-option-group (legacy's printer type, B1467): `q-gutter-x-sm`, so 8 px between 40 px rows that wrap with no row gap.
      className={inline ? (optionGroup ? 'flex flex-wrap gap-x-2' : 'flex flex-wrap gap-x-6 gap-y-3') : optionGroup ? 'flex flex-col' : 'flex flex-col gap-4'}
    >
      {options.map((o) => {
        const itemId = `${label}-${o.value}`.replace(/\W+/g, '-');
        const off = disabled === true || o.disabled === true;
        // A q-radio row is at least 40 px and grows with a wrapped label, as Customer Tips' first option at 390 (B1946).
        return (
          <div key={o.value} className={`${optionGroup ? 'flex min-h-10 items-center gap-2.5' : 'flex items-center gap-3'}${off ? ' cursor-not-allowed opacity-75' : ''}`}>
            <RadixRadio.Item
              id={itemId}
              value={o.value}
              disabled={o.disabled}
              className={`${optionGroup ? 'ml-2.5 ' : ''}inline-flex size-5 items-center justify-center rounded-full border-2 border-text-2 bg-surface data-[state=checked]:border-primary disabled:cursor-not-allowed`}
            >
              <RadixRadio.Indicator className="size-2.5 rounded-full bg-primary" />
            </RadixRadio.Item>
            <label htmlFor={itemId} className={`${off ? 'cursor-not-allowed' : 'cursor-pointer'} text-[14px] ${o.value === value ? `${plainLabels ? '' : 'font-medium '}text-text-1` : 'text-text-2'}`}>{o.label}</label>
          </div>
        );
      })}
    </RadixRadio.Root>
  );
}

interface ToggleProps {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly label: string;
  readonly disabled?: boolean;
  readonly id?: string;
  /**
   * Leave unset for legacy's ToggleInput (the 40×20 track, used on most pages). `md` and `xl` draw Quasar's q-toggle
   * at that size instead, for the few legacy pages that use a bare q-toggle (Add Tender Type at xl, Add/Edit Device's
   * billing toggle at md).
   */
  readonly size?: QToggleSize;
  /** The q-toggle's colour when on (legacy `color`); only with `size`. */
  readonly color?: 'primary' | 'green';
}

type QToggleSize = 'md' | 'xl';

/** Quasar's q-toggle size option, as the inner box's font-size in px (QToggle.sass; option sizes md 40, xl 60). */
const Q_TOGGLE_EM: Readonly<Record<QToggleSize, number>> = { md: 40, xl: 60 };

/** Quasar's `$green` as the legacy back office redefines it (`quasar.variables_modified.scss`), measured on Add Tender Type. */
const LEGACY_GREEN = '#04c635';

/**
 * Quasar q-toggle, measured on legacy's Add Tender Type (xl) and Add Device (md): a 1.4em × 1em box, a .8em × .35em
 * track at (.3em, .325em) in currentColor at 38 % off and 54 % on, a .5em thumb at (.25em, .25em) that moves to .65em,
 * white with Quasar's shadow off and the colour on. Off is the inherited secondary text colour; disabled is 75 %.
 */
function QToggle({ checked, onCheckedChange, label, disabled, id, size, color = 'primary' }: ToggleProps & { readonly size: QToggleSize }) {
  const em = Q_TOGGLE_EM[size];
  const on = color === 'green' ? LEGACY_GREEN : 'var(--color-primary)';
  return (
    <Switch.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      data-size={size}
      className="group relative inline-block shrink-0 cursor-pointer text-text-2 outline-none disabled:cursor-not-allowed disabled:opacity-75"
      style={{ width: em * 1.4, height: em, ...(checked ? { color: on } : {}) }}
    >
      <span
        aria-hidden
        data-part="track"
        className="absolute bg-current transition-opacity"
        style={{ left: em * 0.3, top: em * 0.325, width: em * 0.8, height: em * 0.35, borderRadius: em * 0.175, opacity: checked ? 0.54 : 0.38 }}
      />
      <Switch.Thumb
        data-part="thumb"
        className="absolute block before:absolute before:inset-0 before:scale-0 before:rounded-full before:bg-current before:opacity-[.12] before:transition-transform group-hover:before:scale-200 group-focus-visible:before:scale-200 group-disabled:before:hidden"
        style={{ top: em * 0.25, left: checked ? em * 0.65 : em * 0.25, width: em * 0.5, height: em * 0.5, transition: 'left .22s cubic-bezier(.4,0,.2,1)' }}
      >
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full ${checked ? 'bg-current' : 'bg-white'}`}
          style={{ boxShadow: '0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12)' }}
        />
      </Switch.Thumb>
    </Switch.Root>
  );
}

/**
 * W11 ToggleInput: a 40×20 track, an 18 px knob, primary when on. With `size`, Quasar's q-toggle instead. The off track is
 * legacy's `.slider` --q-border (white at 38 % in the dark theme, $whiteDisabled), its knob a flat white 18 px circle 2 px
 * from the left and 18 px further when on (B807). `label` names the switch for assistive tech only: the caller draws the
 * visible words beside it (legacy pairs ToggleInput with its own heading, B787). Disabled, it fades to 0.6 with the not-allowed
 * cursor: ToggleInput puts `disabled` on its label.switch, which takes Quasar's global `.disabled` (visibility.sass), and its
 * own `.slider.disabled` dark rules never match (B1390).
 */
export function Toggle(props: ToggleProps) {
  if (props.size !== undefined) return <QToggle {...props} size={props.size} />;
  const { checked, onCheckedChange, label, disabled, id } = props;
  return (
    <Switch.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className="relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full bg-line transition-colors data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:opacity-60 dark:data-[state=unchecked]:bg-[#ffffff61]"
    >
      <Switch.Thumb className="block size-[18px] translate-x-[2px] rounded-full bg-white transition-transform data-[state=checked]:translate-x-[20px]" />
    </Switch.Root>
  );
}

// Legacy's dense TypeToggleInput is `width: fit-content` below 1024 (its xs and sm media blocks): the group is as wide as
// its labels and spread splits it evenly from a 0 basis with no minimum (the Schedule's Day/Week, 118.3 wide, 58.7 and 59.7 at 390 and 1023), with no
// 80 px floor. From 1024 it is `width: auto`, where the Schedule's 160 px box splits it in two (79.5 and 80.5). The labels
// draw at 500 at every width: the unpressed q-btn's `font-weight: bolder` is overridden on its `span.block`
// (500 !important), read on GG LA (B1966).
const DENSE_WIDTH = 'min-w-0 flex-1 min-[1024px]:min-w-20';
// The normal one is `width: auto` below 600 and from 1024 (it fills its cell) but `width: fit-content` from 600 to 1023
// (the modifier set's Optional/Required, 179.4 wide in a 512 px cell at 700; B1967).
const NORMAL_GROUP_WIDTH = 'min-[600px]:max-[1024px]:w-fit';

interface SegmentedToggleProps<V extends string> {
  readonly label: string;
  readonly value: V;
  readonly options: readonly Option<V>[];
  readonly onValueChange: (value: V) => void;
  readonly className?: string;
  /** Legacy's `is-dense` (`ui-toggle-type-dense`): 40 px tall with 12 px labels (the Schedule's Day/Week, device settings; B636). */
  readonly dense?: boolean;
  /**
   * 56 px, stretched to the outlined field beside it: legacy's discount dialogs wrap TypeToggleInput in
   * `.type-toggle-input-wrapper > .q-btn-group { height: auto !important }` (PresetDiscountsItem.vue:210,
   * AutoDiscountsItem.vue:687, PriceAdjustmentItem.vue:767, PlanDiscount.vue:191), so their %/$ group takes the 56 px
   * MoneyInput / PercentInput's height (#2379 review).
   */
  readonly field?: boolean;
  /**
   * Each segment as wide as its label (padded 0 16), with no 80 px floor: legacy's dense TypeToggleInput where nothing widens
   * it (device settings' Left/Top is 110 wide, the kitchen font size 208; B1096). Without it a dense toggle is label-wide
   * below 1024 and keeps the 80 px floor and the equal split from 1024 (the Schedule's Day/Week in legacy's 160 px box; B1966).
   */
  readonly fit?: boolean;
}

/**
 * W12 TypeToggleInput: two or more square segments, the active one tinted primary. Legacy's pressed button takes
 * --q-brand-primary-alternate (#e8f1e4, dark #414241) with primary text and border; the others are transparent in --q-text-secondary; every label at weight 500 (`span.block` 500 !important) (B619).
 */
export function SegmentedToggle<V extends string>({ label, value, options, onValueChange, className = '', dense = false, field = false, fit = false }: SegmentedToggleProps<V>) {
  return (
    // Legacy TypeToggleInput's group is 40 px dense (`ui-toggle-type-dense`) and 48 px otherwise (`ui-toggle-type-normal`),
    // unless a caller stretches it (`field`). Each q-btn draws its own 1 px border, rounded 8 at the group's ends, and the pressed one's primary border
    // is its outer edge (no grey frame around it); neighbours share one line (B1429).
    <div role="radiogroup" aria-label={label} className={`inline-flex ${dense ? 'h-10' : field ? 'h-14' : `h-12 ${NORMAL_GROUP_WIDTH}`} ${className}`}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${label} ${o.label}`}
            onClick={() => onValueChange(o.value)}
            className={`${fit ? '' : dense ? DENSE_WIDTH : 'min-w-20 flex-1'} ${dense ? 'text-[12px]' : 'text-[14px]'} -ml-px border px-4 font-medium first:ml-0 first:rounded-l-q last:rounded-r-q ${active ? 'relative border-primary bg-positive-light text-positive-ink dark:bg-primary-alt dark:text-primary' : 'border-line bg-transparent text-text-2'}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
